import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import SeriesRadarChart, {
  seriesItemsFromLegendMaps,
  ASSESSMENT_SERIES_FILTER_MAX,
} from 'js/components/Charts/SeriesRadarChart';
import CohortLensPicker from './CohortLensPicker';
import {
  buildLensPickerItems,
  buildLensRadarSeries,
  combinedLensPickerItem,
} from './multi-assessment-radar-utils';
import {
  ASSESSMENT_SERIES_PALETTE,
  questionSetsHaveResponses,
} from '../assessment-radar-series';
import standardCohortKeys from 'js/utils/cohort-keys';
import styles from './MultiAssessmentRadarChart.module.scss';

function MultiAssessmentRadarChart({
  assessments = [],
  dataMap,
  categoryLookups,
  categoryColorMap,
}) {
  const picker = useMemo(
    () => buildLensPickerItems(assessments),
    [assessments]
  );

  const [selectedId, setSelectedId] = useState(standardCohortKeys.CUMULATIVE);

  const assessmentKeys = (assessments ?? []).map((item) => item.id).join(',');

  useEffect(() => {
    setSelectedId(standardCohortKeys.CUMULATIVE);
  }, [assessmentKeys]);

  useEffect(() => {
    const selected = picker.items.find((item) => item.id === selectedId);
    if (!selected || selected.disabled) {
      setSelectedId(standardCohortKeys.CUMULATIVE);
    }
  }, [picker.items, selectedId]);

  const selectedItem =
    picker.items.find((item) => item.id === selectedId) ??
    picker.combined ??
    combinedLensPickerItem();

  const { legendFields, seriesDataMaps, combinedQuestionSets } = useMemo(
    () =>
      buildLensRadarSeries({
        assessments,
        dataMap,
        lensKey: selectedItem.lensKey,
        selectedBreakout: selectedItem.breakout,
      }),
    [
      assessments,
      dataMap,
      selectedItem.id,
      selectedItem.lensKey,
      selectedItem.breakout,
    ]
  );

  const series = useMemo(
    () => seriesItemsFromLegendMaps(legendFields, seriesDataMaps),
    [legendFields, seriesDataMaps]
  );

  const resetPinKey = `${assessmentKeys}:${selectedItem.id}`;

  const handleSelect = useCallback((item) => {
    if (item?.id) {
      setSelectedId(item.id);
    }
  }, []);

  return (
    <div className={styles.root}>
      {picker.showPicker ? (
        <div className={classNames('manage-org', styles.pickerShell)}>
          <CohortLensPicker
            items={picker.items}
            selectedItem={selectedItem}
            onSelect={handleSelect}
          />
        </div>
      ) : null}
      <SeriesRadarChart
        questionSets={
          questionSetsHaveResponses(combinedQuestionSets)
            ? combinedQuestionSets
            : dataMap?.questionSets ?? combinedQuestionSets ?? {}
        }
        series={series}
        categoryLookups={categoryLookups}
        categoryColorMap={categoryColorMap}
        legendHeading="Assessments"
        seriesPalette={ASSESSMENT_SERIES_PALETTE}
        resetPinKey={resetPinKey}
        enableSeriesFilter
        maxSeries={ASSESSMENT_SERIES_FILTER_MAX}
      />
    </div>
  );
}

export default MultiAssessmentRadarChart;
